interface ImageDimensionsProps {
  left: string | number;
  top: string | number;
  right: string | number;
  bottom: string | number;
}

interface ImageSize {
  width: number;
  height: number;
}

interface ImageUtilsType {
  IMAGE_MAX_WIDTH: number;
  parseImageSizes: (imageDimensions: ImageDimensionsProps) => ImageSize;
  scaleImageSizes: (
    originalWidth: number,
    originalHeight: number,
    maxWidth: number,
    maxHeight: number
  ) => ImageSize;
  base64ArrayBuffer: (arrayBuffer: any) => string;
}

const ImageUtils: ImageUtilsType = {
  IMAGE_MAX_WIDTH: 602.0,

  /**
   * Computes image width and height from image corner points.
   *
   * @param imageDimensions {{left, top, right, bottom}}
   * Image corner points (left, top, right, bottom).
   * @returns {{width, height}} Image size.
   */
  parseImageSizes(imageDimensions) {
    return {
      width:
        parseInt(imageDimensions.right.toString() || '0', 10) -
        parseInt(imageDimensions.left.toString() || '0', 10),
      height:
        parseInt(imageDimensions.bottom.toString() || '0', 10) -
        parseInt(imageDimensions.top.toString() || '0', 10),
    };
  },

  /**
   * Scales image to the maximal width size. And optionally to maximal height size.
   *
   * @param originalWidth The original width of the image.
   * @param originalHeight The original height of the image.
   * @param maxWidth The maximal width of the image.
   * @param maxHeight The maximal height of the image.
   * @returns The image size.
   */
  scaleImageSizes(originalWidth, originalHeight, maxWidth, maxHeight) {
    const scaledMaxWidth = maxWidth || this.IMAGE_MAX_WIDTH;
    const scaleWidthRatio =
      originalWidth > scaledMaxWidth ? scaledMaxWidth / originalWidth : 1;
    const result = {
      width: Math.floor(originalWidth * scaleWidthRatio),
      height: Math.floor(originalHeight * scaleWidthRatio),
    };
    if (maxHeight) {
      const scaleHeightRatio =
        result.height > maxHeight ? maxHeight / result.height : 1;
      result.height = Math.floor(result.height * scaleHeightRatio);
      result.width = Math.floor(result.width * scaleHeightRatio);
    }
    return result;
  },

  /* eslint-disable no-bitwise */
  /**
   * Converts an ArrayBuffer directly to base64, without any intermediate 'convert to string then
   * use window.btoa' step. According to my tests, this appears to be a faster approach:
   * http://jsperf.com/encoding-xhr-image-data/5
   * @see {@link https://gist.github.com/jonleighton/958841}
   * @param arrayBuffer
   * @returns
   * @license
   * MIT LICENSE
   * Copyright 2011 Jon Leighton
   * Permission is hereby granted, free of charge, to any person obtaining a copy of this software
   * and associated documentation files (the "Software"), to deal in the Software without
   * restriction, including without limitation the rights to use, copy, modify, merge, publish,
   * distribute, sublicense, and/or sell copies of the Software, and to permit persons to whom
   * the Software is furnished to do so, subject to the following conditions:
   *
   * The above copyright notice and this permission notice shall be included in all copies or
   * substantial portions of the Software.
   *
   * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED,
   * INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR
   * PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE
   * FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR
   * OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR
   * OTHER DEALINGS IN THE SOFTWARE.
   */
  base64ArrayBuffer(arrayBuffer) {
    let base64 = '';
    const encodings =
      'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';

    const bytes = new Uint8Array(arrayBuffer);
    const {byteLength} = bytes;
    const byteRemainder = byteLength % 3;
    const mainLength = byteLength - byteRemainder;

    let a;
    let b;
    let c;
    let d;
    let chunk;

    // Main loop deals with bytes in chunks of 3
    for (let i = 0; i < mainLength; i += 3) {
      // Combine the three bytes into a single integer
      chunk = (bytes[i] << 16) | (bytes[i + 1] << 8) | bytes[i + 2];

      // Use bitmasks to extract 6-bit segments from the triplet
      a = (chunk & 16515072) >> 18; // 16515072 = (2^6 - 1) << 18
      b = (chunk & 258048) >> 12; // 258048 = (2^6 - 1) << 12
      c = (chunk & 4032) >> 6; // 4032 = (2^6 - 1) << 6
      d = chunk & 63; // 63 = 2^6 - 1

      // Convert the raw binary segments to the appropriate ASCII encoding
      base64 += encodings[a] + encodings[b] + encodings[c] + encodings[d];
    }

    // Deal with the remaining bytes and padding
    if (byteRemainder === 1) {
      chunk = bytes[mainLength];

      a = (chunk & 252) >> 2; // 252 = (2^6 - 1) << 2

      // Set the 4 least significant bits to zero
      b = (chunk & 3) << 4; // 3   = 2^2 - 1

      base64 += `${encodings[a]}${encodings[b]}==`;
    } else if (byteRemainder === 2) {
      chunk = (bytes[mainLength] << 8) | bytes[mainLength + 1];

      a = (chunk & 64512) >> 10; // 64512 = (2^6 - 1) << 10
      b = (chunk & 1008) >> 4; // 1008  = (2^6 - 1) << 4

      // Set the 2 least significant bits to zero
      c = (chunk & 15) << 2; // 15    = 2^4 - 1

      base64 += `${encodings[a]}${encodings[b]}${encodings[c]}=`;
    }

    return base64;
  },
  /* eslint-enable no-bitwise */
};

export default ImageUtils;
